import {
    again,
    boot,
    createEraroError,
    eraroCodesMap,
} from '@dazn/bootstrap-lib';

import { isBeta } from '../configuration/constants';

import { GetDevMode, GetGuid } from './common-types';
import { getHeaders } from './getHeaders';
import { decodeToken } from './token';

const RETRY_BACKOFF_MS = 200;
const FORBIDDEN_CODE = 403;

/**
 *  @see [Request](../../../docs/api-call.md)
 *
 *
 */

function removeInvalidUserProfiles(currentKey: string) {
    const keyPrefix = 'userprofile_';

    for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key?.startsWith(keyPrefix) && key !== currentKey) {
            localStorage.removeItem(key);
        }
    }
}

const setProfileInLocalStorage = (
    fetchData: () => Promise<any>,
    key: string,
    setProfile: (
        newUserProfile: boot.user.UserProfile,
    ) => Record<string, never>,
) =>
    fetchData().then(data => {
        const item = {
            profile: data,
            timeStamp: Date.now(),
        };
        localStorage.setItem(key, JSON.stringify(item));

        if (setProfile) {
            setProfile(data);
        }
        return data;
    });

export const fetchUserProfile = (
    url: string,
    token: string,
    fetch: boot.util.Fetch,
    getGuid: GetGuid,
    getDevMode: GetDevMode,
    setProfile: (
        newUserProfile: boot.user.UserProfile,
    ) => Record<string, never>,
    updateProfileAsync?: boolean,
    customHeaders?: {
        isCustomHeadersEnabled?: boolean;
        brandName?: string;
        daznId?: string;
    },
) => {
    const fetchUserProfile = () => {
        const decodedTokenData = decodeToken(token) as Record<string, string>;

        const key = `userprofile_${decodedTokenData.user}_${decodedTokenData.contentCountry}`;

        removeInvalidUserProfiles(key);

        const fetchData = () =>
            fetch(url, {
                headers: getHeaders(
                    token,
                    getGuid,
                    getDevMode,
                    undefined,
                    isBeta,
                    customHeaders,
                ),
                cache: 'no-cache', // allow conditional requests
            })
                .catch(error => Promise.reject(ERROR.requestFailed(error)))
                .then(response => {
                    if (response.status === FORBIDDEN_CODE) {
                        return Promise.reject(
                            ERROR.countryNotAvailable(response.status),
                        );
                    }
                    if (!response.ok) {
                        return Promise.reject(
                            ERROR.responseNotOk(response.status),
                        );
                    }
                    return response
                        .json()
                        .catch(error => Promise.reject(ERROR.jsonParse(error)));
                });

        const dataFromStorage = JSON.parse(localStorage.getItem(key) || '{}');

        if (Object.keys(dataFromStorage).length > 0) {
            const { profile } = dataFromStorage;

            if (!updateProfileAsync) {
                setProfileInLocalStorage(fetchData, key, setProfile);
                return Promise.resolve(profile);
            }
        }
        return setProfileInLocalStorage(fetchData, key, setProfile);
    };

    return again(fetchUserProfile, {
        backoff: RETRY_BACKOFF_MS,
    });
};

const ERROR = {
    countryNotAvailable: (responseStatus: number) =>
        createEraroError(
            'Country not available',
            eraroCodesMap.category.UserProfileService,
            eraroCodesMap.code.RestrictedCountry,
            String(responseStatus),
        ),
    responseNotOk: (responseStatus: number) =>
        createEraroError(
            'User profile responded not okay',
            eraroCodesMap.category.UserProfileService,
            eraroCodesMap.code.GenericError,
            String(responseStatus),
        ),
    jsonParse: (error: Error) =>
        createEraroError(
            'Could not convert user profile response to JSON',
            eraroCodesMap.category.UserProfileService,
            eraroCodesMap.code.GenericError,
            eraroCodesMap.internal.ParserError,
            error,
        ),
    requestFailed: (error: Error) =>
        createEraroError(
            'User profile request failure',
            eraroCodesMap.category.UserProfileService,
            eraroCodesMap.code.GenericError,
            eraroCodesMap.internal.NetworkError,
            error,
        ),
};
